import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Label } from "@/shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import { useToast } from "@/shared/components/ui/use-toast";
import { formatDateBR } from "@/shared/utils/datetime";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { LearningsService } from "../services/learningsService";
import type { LearningFrequency, WorkspaceLearningSettings } from "../types/LearningTypes";

/**
 * A revisão do uso no workspace (AB#87286): ligar ou desligar e com que frequência, na
 * engrenagem da própria aba Learnings (só admin). Mostra também quando rodou e o último erro.
 */
export const LearningsWorkspaceSettings = ({ workspaceId }: { workspaceId: string }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ["learnings-settings", workspaceId];
  const { data: settings, isLoading } = useQuery({
    queryKey,
    queryFn: () => LearningsService.settings(workspaceId),
  });

  const save = async (input: { enabled?: boolean; frequency?: LearningFrequency }) => {
    const previous = queryClient.getQueryData<WorkspaceLearningSettings>(queryKey);
    if (previous) queryClient.setQueryData(queryKey, { ...previous, ...input });
    try {
      queryClient.setQueryData(queryKey, await LearningsService.saveSettings(workspaceId, input));
    } catch (error) {
      if (previous) queryClient.setQueryData(queryKey, previous);
      toast({
        title: t("learnings.settings.saveError"),
        description: resolveApiErrorMessage(error, t),
        variant: "destructive",
      });
    }
  };

  if (isLoading || !settings) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor="learnings-enabled" className="text-sm">
          {t("learnings.settings.enabled")}
        </Label>
        <Switch
          id="learnings-enabled"
          checked={settings.enabled}
          onCheckedChange={(enabled) => void save({ enabled })}
        />
      </div>
      <div className="flex items-center justify-between gap-4">
        <Label className="text-sm">{t("learnings.settings.frequency")}</Label>
        <Select
          value={settings.frequency}
          onValueChange={(frequency) => void save({ frequency: frequency as LearningFrequency })}
          disabled={!settings.enabled}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">{t("learnings.settings.daily")}</SelectItem>
            <SelectItem value="weekly">{t("learnings.settings.weekly")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-muted-foreground">
        {settings.last_run_at
          ? t("learnings.settings.lastRun", { date: formatDateBR(settings.last_run_at) })
          : t("learnings.settings.neverRan")}
      </p>
      {settings.last_error && (
        <p className="text-xs text-destructive">{t("learnings.settings.lastError", { error: settings.last_error })}</p>
      )}
    </section>
  );
};
